import {
  CalendarDateTime,
  fromAbsolute,
  parseDateTime,
  toCalendarDateTime,
} from '@internationalized/date';

export const SCHEDULE_TIME_ZONE_STORAGE_KEY = 'massMessages.displayTimeZone';
export const DEFAULT_SCHEDULE_TIME_ZONE = 'Europe/Kyiv';

const LEGACY_SCHEDULE_TIME_ZONE = 'Europe/Kyiv';
const TIME_ZONE_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/i;
const DISPLAY_TIME_ZONES = [
  { id: 'UTC', name: 'UTC' },
  { id: 'Europe/Kyiv', name: 'Kyiv' },
  { id: 'Europe/London', name: 'London' },
  { id: 'Europe/Warsaw', name: 'Warsaw' },
  { id: 'Europe/Berlin', name: 'Berlin' },
  { id: 'Europe/Istanbul', name: 'Istanbul' },
  { id: 'America/New_York', name: 'New York' },
  { id: 'America/Chicago', name: 'Chicago' },
  { id: 'America/Denver', name: 'Denver' },
  { id: 'America/Los_Angeles', name: 'Los Angeles' },
  { id: 'Asia/Dubai', name: 'Dubai' },
];

function normalizeTimestamp(value: string): string {
  return value.trim().replace(' ', 'T');
}

function isValidTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export function getScheduleTimeZone(): string {
  if (typeof window === 'undefined') return DEFAULT_SCHEDULE_TIME_ZONE;
  const stored = window.localStorage.getItem(SCHEDULE_TIME_ZONE_STORAGE_KEY);
  if (stored && isValidTimeZone(stored)) return stored;

  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidTimeZone(browserTimeZone) ? browserTimeZone : DEFAULT_SCHEDULE_TIME_ZONE;
}

export function saveScheduleTimeZone(timeZone: string): void {
  if (!isValidTimeZone(timeZone)) throw new RangeError(`Invalid time zone: ${timeZone}`);
  window.localStorage.setItem(SCHEDULE_TIME_ZONE_STORAGE_KEY, timeZone);
}

function timeZoneOffsetLabel(timeZone: string): string {
  const offset = new Intl.DateTimeFormat('en', {
    timeZone,
    timeZoneName: 'shortOffset',
  }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;
  return (offset || 'GMT').replace('GMT', 'UTC');
}

export const SCHEDULE_TIME_ZONE_OPTIONS = DISPLAY_TIME_ZONES
  .filter((timeZone) => isValidTimeZone(timeZone.id))
  .map((timeZone) => ({
    id: timeZone.id,
    label: `${timeZoneOffsetLabel(timeZone.id)} - ${timeZone.name}`,
  }));

export function nowInScheduleTimeZone(timeZone = getScheduleTimeZone()): CalendarDateTime {
  return toCalendarDateTime(fromAbsolute(Date.now(), timeZone));
}

/** Parse a UTC DB instant into the selected display zone.
 *  Naive values are accepted only for compatibility with pre-UTC rows and are
 *  interpreted using the former Europe/Kyiv storage contract.
 */
export function parseScheduleTime(
  value: string,
  timeZone = getScheduleTimeZone(),
): CalendarDateTime {
  if (!value) return nowInScheduleTimeZone(timeZone);

  const normalized = normalizeTimestamp(value);
  let epoch: number;
  if (TIME_ZONE_SUFFIX.test(normalized)) {
    epoch = new Date(normalized).getTime();
  } else {
    epoch = parseDateTime(normalized).toDate(LEGACY_SCHEDULE_TIME_ZONE).getTime();
  }
  if (Number.isNaN(epoch)) throw new RangeError(`Invalid schedule timestamp: ${value}`);
  return toCalendarDateTime(fromAbsolute(epoch, timeZone));
}

export function scheduleTimeToEpoch(
  value: CalendarDateTime,
  timeZone = getScheduleTimeZone(),
): number {
  return value.toDate(timeZone).getTime();
}

export function scheduleTimeToApiValue(
  value: CalendarDateTime,
  timeZone = getScheduleTimeZone(),
): string {
  return value.toDate(timeZone).toISOString();
}

export function scheduleDayKey(value: CalendarDateTime): string {
  return [
    String(value.year).padStart(4, '0'),
    String(value.month).padStart(2, '0'),
    String(value.day).padStart(2, '0'),
  ].join('-');
}

export function formatScheduleTime(
  value: CalendarDateTime,
  timeZone = getScheduleTimeZone(),
): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(value.toDate(timeZone));
}

export function formatScheduleDay(
  value: CalendarDateTime,
  timeZone = getScheduleTimeZone(),
): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone,
  }).format(value.toDate(timeZone));
}
